import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import WorkshopInvoiceManager from './WorkshopInvoiceManager';
import WorkshopInvoiceGenerator from './WorkshopInvoiceGenerator';
import { Workshop, WorkshopParticipant } from '../types';
import {
  GraduationCap,
  Plus,
  FileText,
  Users,
} from 'lucide-react';

const WorkshopManager: React.FC = () => {
  const { user } = useUser();
  const [showInvoiceManager, setShowInvoiceManager] = useState(true);
  const [showInvoiceGenerator, setShowInvoiceGenerator] = useState(false);
  const [selectedWorkshop, setSelectedWorkshop] = useState<Workshop | null>(null);
  const [selectedParticipant, setSelectedParticipant] = useState<WorkshopParticipant | null>(null);

  const handleCreateInvoice = () => {
    // Por ahora, creamos un workshop y participant de ejemplo
    // En producción, esto vendría de una lista de talleres y participantes
    const exampleWorkshop: Workshop = {
      id: 'example-workshop',
      userId: user?.uid || '',
      title: 'Taller de Formación',
      description: 'Taller formativo para profesionales',
      date: new Date(),
      price: 100,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const exampleParticipant: WorkshopParticipant = {
      id: 'example-participant',
      userId: user?.uid || '',
      workshopId: 'example-workshop',
      participantName: '',
      participantSurname: '',
      participantEmail: '',
      paid: false,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    setSelectedWorkshop(exampleWorkshop);
    setSelectedParticipant(exampleParticipant);
    setShowInvoiceGenerator(true);
    setShowInvoiceManager(false);
  };

  const handleInvoiceCreated = () => {
    setShowInvoiceGenerator(false);
    setShowInvoiceManager(true);
    setSelectedWorkshop(null);
    setSelectedParticipant(null);
  };

  const handleCloseGenerator = () => {
    setShowInvoiceGenerator(false);
    setShowInvoiceManager(true);
  };

  if (showInvoiceGenerator && selectedWorkshop && selectedParticipant) {
    return (
      <WorkshopInvoiceGenerator
        workshop={selectedWorkshop}
        participant={selectedParticipant}
        onClose={handleCloseGenerator}
        onInvoiceCreated={handleInvoiceCreated}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-soft p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-br from-pastel-blue to-pastel-blue-light p-3 rounded-xl">
              <GraduationCap className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-pastel-gray-dark">
                Talleres Formativos
              </h1>
              <p className="text-pastel-gray-dark text-sm">
                Gestiona talleres formativos y sus facturas (exentas de IVA)
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleCreateInvoice}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-pastel-blue to-pastel-blue-light text-white rounded-lg hover:from-pastel-blue-dark hover:to-pastel-blue transition-all shadow-gentle"
            >
              <Plus className="h-4 w-4" />
              <span>Nueva Factura</span>
            </button>
          </div>
        </div>
      </div>

      {/* Información sobre talleres */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">
              Información sobre Facturación de Talleres
            </h3>
            <p className="text-sm text-blue-800">
              Los talleres formativos están exentos de IVA según la normativa vigente.
              Las facturas se generan automáticamente cuando un participante completa
              el formulario de Google y realiza el pago a través de Stripe. La numeración
              de las facturas de talleres comparte la secuencia con las facturas de pacientes.
            </p>
          </div>
        </div>
      </div>

      {/* Gestor de Facturas */}
      {showInvoiceManager && <WorkshopInvoiceManager />}
    </div>
  );
};

export default WorkshopManager;


